type Environment = "development" | "test" | "production";

interface ApiConfig {
  baseURL: string;
  socketURL: string;
  timeout: number;
}

interface EnvironmentConfig {
  development: ApiConfig;
  test: ApiConfig;
  production: ApiConfig;
}

const getEnvironment = (): Environment => {
  const env = process.env.NODE_ENV;
  if (env === "production") return "production";
  if (env === "test") return "test";
  return "development";
};

const envApiBaseURL = process.env.REACT_APP_API_BASE_URL;
const envSocketURL = process.env.REACT_APP_SOCKET_URL;
const browserOrigin =
  typeof window !== "undefined" ? window.location.origin : "http://localhost:3001";

const configs: EnvironmentConfig = {
  development: {
    baseURL: "http://localhost:3001/api",
    socketURL: "http://localhost:3001",
    timeout: 30000,
  },
  test: {
    baseURL: "http://localhost:3001/api",
    socketURL: "http://localhost:3001",
    timeout: 30000,
  },
  production: {
    baseURL: envApiBaseURL || `${browserOrigin}/api`,
    socketURL: envSocketURL || browserOrigin,
    timeout: 30000,
  },
};

const env = getEnvironment();
const config = configs[env];

export const API_BASE_URL = config.baseURL;
export const SOCKET_URL = config.socketURL;
export const API_TIMEOUT = config.timeout;

export const API_ENDPOINTS = {
  auth: {
    login: "/users/login",
    register: "/users/register",
    logout: "/users/logout",
    profile: "/users/profile",
    updateProfile: "/users/profile",
    password: "/users/password",
  },
  videoProjects: {
    list: "/video-projects",
    create: "/video-projects",
    get: (id: string | number) => `/video-projects/${id}`,
    chat: (id: string | number, stage: "script" | "storyboard") =>
      `/video-projects/${id}/chat?stage=${stage}`,
    update: (id: string | number) => `/video-projects/${id}`,
    delete: (id: string | number) => `/video-projects/${id}`,
    scripts: (id: string | number) => `/video-projects/${id}/scripts`,
    characters: (id: string | number) => `/video-projects/${id}/characters`,
    generateCharacters: (id: string | number) =>
      `/video-projects/${id}/characters/generate`,
    assets: (id: string | number) => `/video-projects/${id}/assets`,
    generateAssets: (id: string | number) =>
      `/video-projects/${id}/assets/generate`,
    assetImage: (
      projectId: string | number,
      assetType: string,
      assetId: number,
    ) => `/video-projects/${projectId}/assets/${assetType}/${assetId}/image`,
    assetReferenceImage: (
      projectId: string | number,
      assetType: string,
      assetId: number,
    ) =>
      `/video-projects/${projectId}/assets/${assetType}/${assetId}/reference-image`,
    referenceImages: (projectId: string | number) =>
      `/video-projects/${projectId}/reference-images`,
    referenceImage: (projectId: string | number, referenceImageId: number) =>
      `/video-projects/${projectId}/reference-images/${referenceImageId}`,
    characterImage: (projectId: string | number, characterId: number) =>
      `/video-projects/${projectId}/characters/${characterId}/image`,
    characterVoices: (projectId: string | number, characterId: number) =>
      `/video-projects/${projectId}/characters/${characterId}/voices`,
    bindCharacterVoice: (
      projectId: string | number,
      characterId: number,
      voiceId: number,
    ) =>
      `/video-projects/${projectId}/characters/${characterId}/voices/${voiceId}/bind`,
    characterVoice: (
      projectId: string | number,
      characterId: number,
      voiceId: number,
    ) =>
      `/video-projects/${projectId}/characters/${characterId}/voices/${voiceId}`,
    frames: (id: string | number) => `/video-projects/${id}/frames`,
    deleteFrame: (projectId: string | number, frameId: number) => `/video-projects/${projectId}/frames/${frameId}`,
    frameImage: (projectId: string | number, frameId: number) =>
      `/video-projects/${projectId}/frames/${frameId}/image`,
    frameVideo: (projectId: string | number, frameId: number) =>
      `/video-projects/${projectId}/frames/${frameId}/video`,
    frameVideoLastFrame: (projectId: string | number, frameId: number) =>
      `/video-projects/${projectId}/frames/${frameId}/video/last-frame`,
    frameVideoFirstFrame: (projectId: string | number, frameId: number) =>
      `/video-projects/${projectId}/frames/${frameId}/video/first-frame`,
    frameImageUpload: (projectId: string | number, frameId: number) =>
      `/video-projects/${projectId}/frames/${frameId}/image-upload`,
  },
  notifications: {
    list: "/notifications",
    get: (id: number) => `/notifications/${id}`,
    unreadCount: "/notifications/unread-count",
    markAsRead: (id: number) => `/notifications/${id}/read`,
    markAllAsRead: "/notifications/read-all",
    delete: (id: number) => `/notifications/${id}`,
    sendAll: "/notifications/send/all",
    sendUser: "/notifications/send/user",
  },
  admin: {
    users: "/admin/users",
    generateUsers: "/admin/users/generate",
    batchDeleteUsers: "/admin/users/batch-delete",
    user: (id: number) => `/admin/users/${id}`,
    content: "/admin/content",
    contentDetail: (id: number) => `/admin/content/${id}`,
    models: "/admin/models",
    model: (id: number) => `/admin/models/${id}`,
    testModel: "/admin/models/test",
    modelConfigs: "/admin/models/configs",
    modelConfig: "/admin/model-config",
    inviteCodes: "/admin/invite-codes",
    ipCharacters: "/admin/ip-characters",
    uploadIpCharacterImage: "/admin/ip-characters/upload",
    ipCharacter: (id: number) => `/admin/ip-characters/${id}`,
    logs: "/logs",
    logStats: "/logs/stats",
    providerAccounts: "/admin/provider-accounts",
    providerAccount: (id: number) => `/admin/provider-accounts/${id}`,
    providerCapability: (id: number) =>
      `/admin/provider-accounts/${id}/capability`,
    providerCapabilityDelete: (id: number) =>
      `/admin/provider-capabilities/${id}`,
    providerSubmissions: "/admin/provider-submissions",
    reconcileProviderSubmission: (taskId: string) =>
      `/admin/provider-submissions/${taskId}/reconcile`,
    monitoringOverview: "/admin/monitoring/overview",
    monitoringTrends: "/admin/monitoring/trends",
    monitoringErrors: "/admin/monitoring/errors",
    monitoringError: (id: string | number) => `/admin/monitoring/errors/${id}`,
    monitoringTasks: "/admin/monitoring/tasks",
    monitoringTask: (id: string) => `/admin/monitoring/tasks/${id}`,
    monitoringAlerts: "/admin/monitoring/alerts",
    pointsUsers: "/admin/points/users",
    pointsUser: (userId: string | number) => `/admin/points/users/${userId}`,
    pointsUserRecords: (userId: string | number) => `/admin/points/users/${userId}/records`,
    pointsTask: (userId: string | number, taskId: string) => `/admin/points/users/${userId}/tasks/${taskId}`,
    adjustUserPoints: (userId: string | number) => `/admin/points/users/${userId}/adjust`,
  },
  ai: {
    history: "/ai/history",
    models: "/ai/models",
    tasks: "/ai/tasks",
    sseTicket: "/ai/sse-ticket",
    modelConfig: "/ai/model-config",
    taskStatus: (id: string) => `/ai/tasks/${id}`,
    cancelTask: (id: string) => `/ai/tasks/${id}/cancel`,
    deleteTask: (id: string) => `/ai/tasks/${id}`,
    submitImageTask: "/ai/images/tasks",
    optimizeImagePrompt: "/ai/images/prompts/optimize",
    ipCharacters: "/ai/ip-characters",
  },
  points: {
    balance: "/points/balance",
    history: "/points/history",
    grant: "/points/grant",
    adminLedger: "/points/admin/ledger",
    requests: "/points/requests",
    adminRequests: "/points/requests/admin",
    approveRequest: (id: number) => `/points/requests/${id}/approve`,
    rejectRequest: (id: number) => `/points/requests/${id}/reject`,
  },
  pay: {
    createOrder: "/pay/recharge",
    orders: "/pay/orders",
    order: (id: string) => `/pay/orders/${id}`,
    callback: "/pay/callback/alipay",
    adminOrders: "/pay/admin/orders",
    reconcileOrder: (id: string) => `/pay/admin/orders/${id}/reconcile`,
    adminRefunds: "/pay/admin/refunds",
    refundOrder: (id: string) => `/pay/admin/orders/${id}/refund`,
  },
  search: {
    query: "/search",
  },
  comments: {
    list: "/comments",
    create: "/comments",
    delete: (id: number) => `/comments/${id}`,
  },
  roles: {
    list: "/roles",
    create: "/roles",
    upload: "/roles/upload",
  },
  materials: {
    library: "/materials/library",
    libraryMaterial: (id: number) => `/materials/library/${id}`,
  },
  videoProjectAssets: {
    list: "/assets/video-projects",
    create: (projectId: string | number) =>
      `/assets/video-projects/${projectId}`,
    import: (assetId: number) => `/assets/video-projects/${assetId}/import`,
    update: (assetId: number) => `/assets/video-projects/${assetId}`,
    delete: (assetId: number) => `/assets/video-projects/${assetId}`,
  },
} as const;

export const getFullURL = (endpoint: string): string => {
  return `${API_BASE_URL}${endpoint}`;
};

export const getSocketURL = (): string => {
  return SOCKET_URL;
};

export type { ApiConfig, Environment };
